import { useEffect, useRef, useState } from 'react'

import type { LineItem, Validation } from '../../../lib/types'

const COLUMNS: Array<{ key: keyof LineItem; head: string; numeric?: boolean; width?: number }> = [
  { key: 'description', head: 'Description', width: 220 },
  { key: 'hsn_sac', head: 'HSN', width: 84 },
  { key: 'quantity', head: 'Qty', numeric: true, width: 62 },
  { key: 'unit', head: 'Unit', width: 54 },
  { key: 'rate', head: 'Rate', numeric: true, width: 78 },
  { key: 'taxable_value', head: 'Taxable', numeric: true, width: 88 },
  { key: 'cgst_percent', head: 'CGST%', numeric: true, width: 60 },
  { key: 'cgst_amount', head: 'CGST', numeric: true, width: 82 },
  { key: 'sgst_percent', head: 'SGST%', numeric: true, width: 60 },
  { key: 'sgst_amount', head: 'SGST', numeric: true, width: 82 },
  { key: 'igst_amount', head: 'IGST', numeric: true, width: 82 },
  { key: 'total', head: 'Total', numeric: true, width: 92 },
]

interface Props {
  items: LineItem[]
  issuesByPath: Record<string, Validation[]>
  confidence: Record<string, number>
  editedPaths: Set<string>
  onCommit: (path: string, value: string | null) => void
  onFocusField?: (path: string) => void
}

function Cell({
  path, value, issues, confidence, edited, numeric, onCommit, onFocusField,
}: {
  path: string
  value: string | null
  issues: Validation[]
  confidence?: number
  edited: boolean
  numeric?: boolean
  onCommit: (path: string, value: string | null) => void
  onFocusField?: (path: string) => void
}) {
  const [local, setLocal] = useState(value ?? '')
  const dirty = useRef(false)

  useEffect(() => { if (!dirty.current) setLocal(value ?? '') }, [value])

  const flag = issues.some((i) => i.severity === 'error')
    ? 'error'
    : issues.some((i) => i.severity === 'warn') || (confidence !== undefined && confidence < 0.8)
      ? 'warn'
      : 'ok'

  const primary = issues.find((i) => i.severity === 'error') ?? issues[0]

  return (
    <input
      className={[
        numeric ? 'n' : '',
        flag !== 'ok' ? `flag-${flag}` : '',
        edited ? 'edited' : '',
      ].filter(Boolean).join(' ')}
      value={local}
      title={primary?.message}
      onChange={(e) => { dirty.current = true; setLocal(e.target.value) }}
      onFocus={() => onFocusField?.(path)}
      onBlur={() => {
        dirty.current = false
        const next = local.trim() === '' ? null : local
        if (next !== value) onCommit(path, next)
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur()
        if (e.key === 'Escape') { dirty.current = false; setLocal(value ?? '') }
      }}
      aria-invalid={flag === 'error'}
      aria-label={path}
    />
  )
}

export default function ItemsGrid({
  items, issuesByPath, confidence, editedPaths, onCommit, onFocusField,
}: Props) {
  if (items.length === 0) {
    return (
      <div className="empty">
        <div className="empty-title">No line items were extracted</div>
        <div>Re-run extraction, or add the rows by hand before approving.</div>
      </div>
    )
  }

  return (
    <div className="items-scroll">
      <table className="items">
        <thead>
          <tr>
            <th style={{ width: 30 }} />
            {COLUMNS.map((c) => (
              <th key={c.key} style={{ width: c.width }}>{c.head}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((item, index) => (
            <tr key={index}>
              <td className="row-no">{index + 1}</td>
              {COLUMNS.map((column) => {
                const path = `items[${index}].${column.key}`
                return (
                  <td key={column.key}>
                    <Cell
                      path={path}
                      value={item[column.key] as string | null}
                      issues={issuesByPath[path] ?? []}
                      confidence={confidence[path]}
                      edited={editedPaths.has(path)}
                      numeric={column.numeric}
                      onCommit={onCommit}
                      onFocusField={onFocusField}
                    />
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
